import React, { useState } from "react";
import { Send, Mail, User, MessageSquare, CheckCircle, Phone } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "./ui/sonner";

const FORM_NAME = "contact";

// Validierung: sinnvolle Regeln pro Feld
const VALIDATION = {
  name: { minLength: 2, maxLength: 100 },
  email: { maxLength: 254 },
  phone: { minLength: 6, maxLength: 25 }, // optional; wenn ausgefüllt, dann Format prüfen
  message: { minLength: 10, maxLength: 2000 },
};

const validateEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// Telefon: optional; wenn angegeben, nur Ziffern, Leerzeichen, +, -, (, ) und Länge
const validatePhone = (phone) => {
  const t = phone.trim();
  if (!t) return true;
  if (t.length < VALIDATION.phone.minLength || t.length > VALIDATION.phone.maxLength) return false;
  return /^[+\d\s\-()]+$/.test(t);
};

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    "bot-field": "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Einzelnes Feld validieren (für Live-Validierung)
  const getFieldError = (name, value, allData = formData) => {
    const data = { ...allData, [name]: value };
    if (name === "name") {
      if (!data.name.trim()) return "Bitte geben Sie Ihren Namen ein.";
      if (data.name.trim().length < VALIDATION.name.minLength) return "Name muss mindestens 2 Zeichen haben.";
      if (data.name.length > VALIDATION.name.maxLength) return `Name darf maximal ${VALIDATION.name.maxLength} Zeichen haben.`;
      return null;
    }
    if (name === "email") {
      if (!data.email.trim()) return "Bitte geben Sie Ihre E-Mail-Adresse ein.";
      if (!validateEmail(data.email)) return "Bitte geben Sie eine gültige E-Mail-Adresse ein.";
      if (data.email.length > VALIDATION.email.maxLength) return "E-Mail-Adresse ist zu lang.";
      return null;
    }
    if (name === "phone") {
      if (!data.phone.trim()) return null;
      if (!validatePhone(data.phone)) return "Bitte geben Sie eine gültige Telefonnummer ein (Ziffern, +, -, Leerzeichen, Klammern).";
      return null;
    }
    if (name === "message") {
      if (!data.message.trim()) return "Bitte geben Sie eine Nachricht ein.";
      if (data.message.trim().length < VALIDATION.message.minLength) return `Nachricht muss mindestens ${VALIDATION.message.minLength} Zeichen haben.`;
      if (data.message.length > VALIDATION.message.maxLength) return `Nachricht darf maximal ${VALIDATION.message.maxLength} Zeichen haben.`;
      return null;
    }
    return null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Live-Validierung: Fehler für dieses Feld sofort aktualisieren
    const nextError = getFieldError(name, value);
    setErrors((prev) => ({ ...prev, [name]: nextError || null }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const nextError = getFieldError(name, value);
    setErrors((prev) => ({ ...prev, [name]: nextError || null }));
  };

  const validate = () => {
    const next = {};
    const nameErr = getFieldError("name", formData.name);
    if (nameErr) next.name = nameErr;
    const emailErr = getFieldError("email", formData.email);
    if (emailErr) next.email = emailErr;
    const phoneErr = getFieldError("phone", formData.phone);
    if (phoneErr) next.phone = phoneErr;
    const messageErr = getFieldError("message", formData.message);
    if (messageErr) next.message = messageErr;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);

    try {
      const payload = new URLSearchParams({
        "form-name": FORM_NAME,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        "bot-field": formData["bot-field"],
      }).toString();

      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload,
      });

      if (response.ok) {
        setSubmitSuccess(true);
        toast.success("Nachricht gesendet", {
          description:
            "Vielen Dank für Ihre Anfrage. Wir melden uns zeitnah bei Ihnen.",
        });
        setFormData({
          name: "",
          email: "",
          phone: "",
          message: "",
          "bot-field": "",
        });
      } else {
        throw new Error("Submit failed");
      }
    } catch (err) {
      toast.error("Fehler", {
        description:
          "Beim Senden ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Toaster position="top-right" />
      <section
        id="kontakt"
        className="section-spacing bg-gradient-to-br from-[#0f1419] via-[#1a2332] to-[#2c4a52] relative overflow-hidden"
      >
        {/* Animated background elements */}
        <div className="absolute top-20 right-0 w-96 h-96 bg-[#4a9da8]/10 rounded-full blur-3xl float-element"></div>
        <div
          className="absolute bottom-20 left-0 w-96 h-96 bg-[#2c4a52]/20 rounded-full blur-3xl float-element"
          style={{ animationDelay: "3s" }}
        ></div>

        <div className="container-custom relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16 fade-in">
              <div className="inline-block mb-4">
                <span className="px-4 py-2 bg-[#4a9da8]/10 text-[#4a9da8] text-sm font-semibold rounded-full border border-[#4a9da8]/20">
                  Kontakt
                </span>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
                Bereit für den
                <br />
                <span className="gradient-text-contact">nächsten Schritt?</span>
              </h2>
              <p className="text-xl text-white/70 leading-relaxed max-w-2xl mx-auto">
                Wenn Sie Ihre Social-Media-Aktivitäten strukturiert auslagern
                möchten, freuen wir uns über ein unverbindliches Gespräch.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-start">
              {/* Left side - Kontaktinfo, neutral und ruhig */}
              <div className="space-y-6 fade-in-left">
                <div className="glass-dark p-8 rounded-2xl">
                  <h3 className="text-xl font-semibold text-white mb-5 tracking-tight">
                    Warum mit uns arbeiten?
                  </h3>
                  <ul className="space-y-5 text-white/80 text-sm leading-relaxed">
                    <li>
                      <span className="text-white font-medium">Strukturierte Betreuung</span>
                      <span className="text-white/60"> — Klare Prozesse und Transparenz in allen Schritten.</span>
                    </li>
                    <li>
                      <span className="text-white font-medium">Messbare Ergebnisse</span>
                      <span className="text-white/60"> — Datenbasierte Optimierung für nachhaltigen Erfolg.</span>
                    </li>
                    <li>
                      <span className="text-white font-medium">Volle Kontrolle</span>
                      <span className="text-white/60"> — Sie behalten jederzeit den Überblick über alle Aktivitäten.</span>
                    </li>
                  </ul>
                  <p className="mt-6 pt-5 border-t border-white/10 text-white/70 text-sm">
                    Wir melden uns innerhalb von 24 Stunden.
                  </p>
                </div>
              </div>

              {/* Right side - Erfolgsmeldung oder Contact form (Netlify Forms) */}
              {submitSuccess ? (
                <div className="glass-dark p-8 rounded-2xl fade-in-right flex flex-col items-center justify-center text-center min-h-[320px]">
                  <div className="w-16 h-16 bg-[#4a9da8]/20 rounded-full flex items-center justify-center mb-6">
                    <CheckCircle className="w-10 h-10 text-[#4a9da8]" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">
                    Nachricht gesendet
                  </h3>
                  <p className="text-white/70 leading-relaxed mb-8 max-w-sm">
                    Vielen Dank für Ihre Anfrage. Wir melden uns innerhalb von 24 Stunden bei Ihnen.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSubmitSuccess(false)}
                    className="text-[#4a9da8] font-semibold hover:text-[#4a9da8]/80 transition-colors underline underline-offset-2"
                  >
                    Weitere Nachricht senden
                  </button>
                </div>
              ) : (
                <form
                  name={FORM_NAME}
                  method="post"
                  data-netlify="true"
                  data-netlify-honeypot="bot-field"
                  onSubmit={handleSubmit}
                  className="glass-dark p-8 rounded-2xl fade-in-right"
                >
                  <p className="text-[#4a9da8] text-sm font-semibold mb-6">Kostenlose Marketing-Beratung – unverbindlich.</p>
                  <input type="hidden" name="form-name" value={FORM_NAME} />
                  {/* Honeypot: von Nutzern unsichtbar, Bots füllen es aus → Netlify verwirft das Submit */}
                  <div className="absolute -left-[9999px]" aria-hidden="true">
                    <label htmlFor="bot-field">Nicht ausfüllen</label>
                    <input
                      id="bot-field"
                      type="text"
                      name="bot-field"
                      value={formData["bot-field"]}
                      onChange={handleChange}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  <div className="space-y-6">
                    <div>
                      <label
                        htmlFor="name"
                        className="flex items-center gap-2 text-white text-sm font-semibold mb-3"
                      >
                        <User className="w-4 h-4 text-[#4a9da8]" />
                        Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        required
                        minLength={VALIDATION.name.minLength}
                        maxLength={VALIDATION.name.maxLength}
                        autoComplete="name"
                        className={`w-full px-4 py-4 bg-white/5 border-2 rounded-xl text-white placeholder-white/40 focus:outline-none focus:bg-white/10 transition-all duration-300 ${
                          errors.name ? "border-red-400/60" : "border-white/10 focus:border-[#4a9da8]"
                        }`}
                        placeholder="Ihr Name"
                        aria-invalid={!!errors.name}
                        aria-describedby={errors.name ? "name-error" : undefined}
                      />
                      {errors.name && (
                        <p id="name-error" className="mt-2 text-sm text-red-300" role="alert">
                          {errors.name}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="email"
                        className="flex items-center gap-2 text-white text-sm font-semibold mb-3"
                      >
                        <Mail className="w-4 h-4 text-[#4a9da8]" />
                        E-Mail
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        required
                        maxLength={VALIDATION.email.maxLength}
                        autoComplete="email"
                        className={`w-full px-4 py-4 bg-white/5 border-2 rounded-xl text-white placeholder-white/40 focus:outline-none focus:bg-white/10 transition-all duration-300 ${
                          errors.email ? "border-red-400/60" : "border-white/10 focus:border-[#4a9da8]"
                        }`}
                        placeholder="ihre@email.de"
                        aria-invalid={!!errors.email}
                        aria-describedby={errors.email ? "email-error" : undefined}
                      />
                      {errors.email && (
                        <p id="email-error" className="mt-2 text-sm text-red-300" role="alert">
                          {errors.email}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="phone"
                        className="flex items-center gap-2 text-white text-sm font-semibold mb-3"
                      >
                        <Phone className="w-4 h-4 text-[#4a9da8]" />
                        Telefon <span className="text-white/50 font-normal">(optional)</span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        maxLength={VALIDATION.phone.maxLength}
                        autoComplete="tel"
                        className={`w-full px-4 py-4 bg-white/5 border-2 rounded-xl text-white placeholder-white/40 focus:outline-none focus:bg-white/10 transition-all duration-300 ${
                          errors.phone ? "border-red-400/60" : "border-white/10 focus:border-[#4a9da8]"
                        }`}
                        placeholder="z. B. +43 664 1234567"
                        aria-invalid={!!errors.phone}
                        aria-describedby={errors.phone ? "phone-error" : undefined}
                      />
                      {errors.phone && (
                        <p id="phone-error" className="mt-2 text-sm text-red-300" role="alert">
                          {errors.phone}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="message"
                        className="flex items-center gap-2 text-white text-sm font-semibold mb-3"
                      >
                        <MessageSquare className="w-4 h-4 text-[#4a9da8]" />
                        Nachricht
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        required
                        minLength={VALIDATION.message.minLength}
                        maxLength={VALIDATION.message.maxLength}
                        rows="5"
                        className={`w-full px-4 py-4 bg-white/5 border-2 rounded-xl text-white placeholder-white/40 focus:outline-none focus:bg-white/10 resize-none transition-all duration-300 ${
                          errors.message ? "border-red-400/60" : "border-white/10 focus:border-[#4a9da8]"
                        }`}
                        placeholder="Ihre Nachricht (mind. 10 Zeichen)"
                        aria-invalid={!!errors.message}
                        aria-describedby={errors.message ? "message-error" : undefined}
                      />
                      {errors.message && (
                        <p id="message-error" className="mt-2 text-sm text-red-300" role="alert">
                          {errors.message}
                        </p>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-[#2c4a52] to-[#4a9da8] text-white text-lg font-semibold rounded-xl hover:shadow-2xl hover:shadow-[#4a9da8]/20 hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                    >
                      {isSubmitting ? "Wird gesendet..." : "Digitale Strategie anfragen"}
                      {!isSubmitting && <Send className="w-5 h-5" />}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>

        <style jsx>{`
          .gradient-text-contact {
            background: linear-gradient(135deg, #4a9da8, #e8e4dc);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }
        `}</style>
      </section>
    </>
  );
};

export default ContactSection;
